import { Injectable , inject} from '@angular/core';
import { Observable, of, timer, switchMap, throwError, map, catchError, firstValueFrom } from 'rxjs';
import { mapTo, tap } from 'rxjs/operators';
import { EventEmitter } from '@angular/core';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';

import { AuthRepository, AuthState, ReturnAuthState, User } from './auth.repository';
import { AuthUtils } from './auth.utils';

import { JournalService } from '@app/core/journal.service';
import { MailService } from '@app/core/mail.service';
import { CookieService } from 'ngx-cookie-service';
import { Logger } from '@app/core/logger.service';
import { FeatureSettingsService } from '@app/services/feature-settings.service';

// vclassroom
import { AppConfig, ApiUrl } from 'app/config';
import { RoomService } from 'app/services/vclassroom/room.service';
//import { SignalingService } from 'app/services/vclassroom/signaling.service';
import { UserRole } from 'app/models/role.enum';
//import { LogService } from 'app/services/vclassroom/log.service';
//import { VCUser } from '@app/models/user.interface';
import { VirtualClassroomService } from '@app/services/vclassroom.service';
import { PresenceService } from '@app/services/presence.service';

//--
const log = new Logger('AuthHttpService');

import { AppConfigService } from 'app/core/config/app-config.service';

export interface Backdoor {
  uid: string;
  token: string;
  tstamp: string;
}

export interface Credentials {
  email: string;
  password: string;
  remember_me: string;
}
const HttpUploadOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
  }),
};

interface AuthRequest {
  userIdentity: string;
  password: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthHttpService {
  private _authenticated: boolean = false;
  private _user: User | null = null;

  public authStateEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
  //private _vcuser: VCUser | null = null;
  //private _isAuthorized: boolean = false;

  // constructor
  constructor(
    private http: HttpClient,
    private authRepository: AuthRepository,
    private journalService: JournalService,
    private mailService: MailService,
    private cookieService: CookieService,
    private vclassService: VirtualClassroomService,
    private featureSettingsService: FeatureSettingsService,
    private presenceService: PresenceService,
    readonly appConfig: AppConfigService
    //private readonly signalingService: SignalingService,
    //private readonly logger: LogService
  ) {}

  public login(creds: Credentials): Observable<ReturnAuthState> {
    const data = {
      email: creds.email,
      password: creds.password,
      rememberMe: creds.remember_me,
      vclass: this.appConfig.get<boolean>('feature_classroom', false).toString(),
    };

    // rest api call
    return this.http.post<any>(`${this.appConfig.get('serverUrl')}/v5/login`, JSON.stringify(data), HttpUploadOptions).pipe(
      map((res) => {
        console.log(res);

        // check for error
        if (res.status === 'ERROR') {
          console.log('an error occured');

          return of(false);
        }

        let data = res.data;

        // we have to do some mapping
        // as the return is more than
        // we want in the auth store
        let auth = {
          access_token: data.access_token,
          token_type: data.token_type,
          expires_in: data.expires_in,
          refresh_token: data.refresh_token,
          access_point: data.access_point,
          customer_key: data.customer_key,

          user: {
            name: data.name,
            company_id: data.company_id,
            email: data.email,
            role: data.role,
            profile_image: data.profile_image,

            firststart: data.firststart,
            firststart_done: data.firststart_done,
            user_id: data.userid,
            person_id: data.fid,

            isactivated: data.isactivated,
            isapproved: data.isapproved,
            status: data.status,

            country: data.country,

            phone: data.phone,
            company: data.company,
            lang: data.lang,
            badges_earned: data.badges_earned,
            new_messages: data.new_messages,

            supervisor_id: data.supervisor_id,
            supervisor_name: data.supervisor_name,
            supervisor_avatar: data.supervisor_avatar,
            supervisor_roomkey: data.supervisor_room_key,

            twin_id: data.twin_id,
            twin_name: data.twin_name,
            twin_avatar: data.twin_avatar,

            // RBAC Permission System fields
            permissions: data.permissions || [],
            roles: data.roles || [],
            is_admin: data.is_admin || false,

            // Company access fields
            allowed_companies: data.allowed_companies || [],
            allowed_courses: data.allowed_courses || [],
            is_global_admin: data.is_global_admin || false,
            primary_company_id: data.primary_company_id || null,
          },

          appdata: {
            version: data.version,
            appdata: data.appdata,
          },
        };

        /*
        export type User = {
        role: number;
        name: string;
        email: string;
        firststart_done: number;
        userid: number;
        status: number;
        company: string;
        fid: number;
        firststart: string;
        isapproved: number;
        isactivated: number;
        profile_image: string;
        country: string;
        };
        */

        this._user = {
          name: data.name,
          company_id: data.company_id,
          email: data.email,
          role: data.role,
          profile_image: data.profile_image,

          firststart: data.firststart,
          firststart_done: data.firststart_done,
          user_id: data.userid,
          person_id: data.fid,

          isactivated: data.isactivated,
          isapproved: data.isapproved,
          status: data.status,

          country: data.country,

          phone: data.phone,
          company: data.company,
          lang: data.lang,
          badges_earned: data.badges_earned,
          new_messages: data.new_messages,

          supervisor_id: data.supervisor_id,
          supervisor_name: data.supervisor_name,
          supervisor_avatar: data.supervisor_avatar,
          supervisor_roomkey: data.supervisor_room_key,

          twin_id: data.twin_id,
          twin_name: data.twin_name,
          twin_avatar: data.twin_avatar,

          // RBAC Permission System fields
          permissions: data.permissions || [],
          roles: data.roles || [],
          is_admin: data.is_admin || false,

          // Company access fields
          allowed_companies: data.allowed_companies || [],
          allowed_courses: data.allowed_courses || [],
          is_global_admin: data.is_global_admin || false,
          primary_company_id: data.primary_company_id || null,
        };

        // save it to the auth repository /store
        this.authRepository.save(auth);
        this.presenceService.startPresence(data.presence_session_uuid);

        // Store system & company settings from login response (encrypted in localStorage)
        this.featureSettingsService.initFromLoginResponse(data);

        // and now login the user to the signaling server
        // Note: Video will NOT start here - it will start when user enters a course (academy route)

        if (this.appConfig.get<boolean>('feature_classroom', false) && data?.supervisor_room_key) {
          // this is not valid anymore - janus webrtc server
          // login to the classroom system
          //this.vclassLogin('student1@example.com', '123456');

          // Usage
          const sanitizedName = this.sanitize(data.name);
          const randomHex = this.genRandomHex();
          const identity = `${sanitizedName}-student-${data.company_id}.${data.fid}-${randomHex}`;

          // Initialize session but DON'T auto-join - video will start when user enters course
          console.log('🔍 [AuthHttpService] Initializing VirtualClassroom session (without auto-join) with data:', {
            name: data.name,
            fid: data.fid,
            company_id: data.company_id,
            supervisor_room_key: data.supervisor_room_key,
            twin_id: data.twin_id,
            fullData: data
          });
          
          this.vclassService.initializeSession({
            name: data.name,
            fid: data.fid,
            company_id: data.company_id,
            supervisor_room_key: data.supervisor_room_key,
            twin_id: data.twin_id,
          }, false); // false = don't auto-join, wait until user enters course
        }

        return res;
      })
    );
  }

  // Helper to sanitize a string (alphanumeric + dashes)
  sanitize(input: string): string {
    return input.toLowerCase().replace(/[^a-z0-9\-]/g, '-');
  }

  // Helper to generate random 12-character hex
  genRandomHex(length = 12): string {
    return Array.from(crypto.getRandomValues(new Uint8Array(length / 2)))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /*
  virtual classroom functionality
  -------------------------------------------------------
  * janus llogin not needed anymore
  public async vclassLogin(userIdentity: string, password: string): Promise<void> {
    const url = `${AppConfig.backendHost}${ApiUrl.userLogin}`;
    return new Promise((resolve: () => void, reject: (reason?: string) => void) => {
      firstValueFrom(
        this.http.post<VCUser>(url, { userIdentity, password } as AuthRequest).pipe(
          map((vcUser: VCUser) => {
            this.authInSignaling(userIdentity, password)
              .then(() => {
                this.logger.log(`Authorization successful: ${userIdentity}`);
                this._isAuthorized = true;
                const roleName: string = vcUser.role[0].toUpperCase() + vcUser.role.slice(1);
                this._vcuser = {
                  ...vcUser,
                  role: UserRole[roleName as keyof typeof UserRole],
                } as VCUser; // User.fromJSON(user);
                this.authStateEvent.emit(true);
                localStorage.setItem('isAuthorized', 'true');
                localStorage.setItem('currentUser', JSON.stringify(vcUser));
                // TODO: Refactor this!!!!!!
                //localStorage.setItem('password', password);
                resolve();
              })
              .catch((error) => {
                // reject(`Can't authorize in the signaling server: ${error}`);
                throwError(() => new Error(`Can't authorize in the signaling server: ${error}`));
              });
          }),
          catchError((error: HttpErrorResponse) => {
            // reject(error.message);
            return this.handleError(error);
          })
        )
      ).catch((error: Error) => reject(error.message));
    });
  }
*/

  /* old janus webrtc server
  public async authInSignaling(userIdentity: string, password: string): Promise<void> {
    return this.signalingService.login(userIdentity, password!);
  }
  *
  public vclassLogout(): void {
    this._vcuser = null;
    //this._isAuthorized = false;
    this.authStateEvent.emit(false);
    localStorage.removeItem('isAuthorized');
    localStorage.removeItem('currentUser');
    // TODO: Refactor this!!!!!!
    //localStorage.removeItem('password');
  }
  */

  /**
   * -------------------------------------
   */

  public firststart(inputdata: any): Observable<any> {
    let input = new FormData();
    // Add your values in here
    //input.append('uid', inputdata.uid);
    input.append('token', inputdata.token);
    //input.append('tstamp', inputdata.tstamp);

    console.log('----------------xxxx');
    console.log(inputdata);

    // rest api call
    return this.http.post<any>(`${this.appConfig.get('serverUrl')}/v5/firststart`, JSON.stringify(inputdata), HttpUploadOptions).pipe(
      map((data) => {
        //console.log(data);

        if (data.firststart_done == '1') {
          // yes firststart was done
          // update the repository

          let user = this.authRepository.getUser();
          let profile: User = {
            ...user,
            company_id: data.profile?.company ?? user.company_id,
            email: data.profile?.email ?? user.email,
            profile_image: data.profile?.profile_image ?? user.profile_image,
            firststart_done: +data.firststart_done,
            isactivated: +data.isactivated,
            isapproved: +(data.profile?.approved ?? user.isapproved),
          };
          this.authRepository.setUser(profile);
        }

        return data;
      })
    );
  }

  /**
   * Synchronously clears all local auth state without making an HTTP call.
   * Use as a fallback when the logout HTTP request fails (e.g. offline).
   */
  logoutLocal(): void {
    this.authRepository.logout();
    this.featureSettingsService.clearSettings();
    this.presenceService.stopPresence(false);
  }

  /**
   * logout of the system
   * @returns
   */
  logout() {
    let data = {};

    return this.http.post<any>(`${this.appConfig.get('serverUrl')}/v5/logout`, JSON.stringify(data), HttpUploadOptions).pipe(
      map((data) => {
        //console.log(data);
        console.log('logout http-service');

        this.authRepository.logout();
        this.featureSettingsService.clearSettings();
        this.presenceService.stopPresence(true);

        // and now login the user to the signaling server
        //this.vclassLogout();

        return data;
      })
    );
  }

  changePassword(data: any) {
    // rest api call
    return this.http.post<any>(`${this.appConfig.get('serverUrl')}/v5/change-pwd`, JSON.stringify(data), HttpUploadOptions).pipe(
      map((data) => {
        //console.log(data);
        return data;
      })
    );
  }

  /**
   * @deprecated Legacy /v5/forgot-pwd endpoint has been retired. Use the modern
   * 6-digit reset flow instead (ForgotPasswordComponent → /v5/forgot-password,
   * /v5/verify-code, /v5/reset-password). Kept only so orphaned legacy components
   * still compile; do not call in new code.
   */
  forgotPassword(data: any) {
    // rest api call
    return this.http.post<any>(`${this.appConfig.get('serverUrl')}/v5/reset-password`, JSON.stringify(data), HttpUploadOptions).pipe(
      map((data) => {
        //console.log(data);
        return data;
      })
    );
  }

  /**
   * Check the authentication status
   */

  /**
   * Check the authentication status
   */
  //_authenticated: false;
  _firstStart_done: false;
  checkLogin(): Observable<boolean> {
    console.log('check login');

    // Check if the user is logged in
    if (this.authRepository.isLoggedIn()) {
      this.presenceService.startPresence();
      return of(true);
    }

    // Check the access token availability
    if (!this.authRepository.getAccessToken()) {
      return of(false);
    }

    // Check the access token expire date
    if (AuthUtils.isTokenExpired(this.authRepository.getAccessToken())) {
      this.presenceService.stopPresence(false);
      return of(false);
    }

    // If the access token exists, and it didn't expire, sign in using it
    return this.signInUsingToken();
  }

  checkFirststart(): Observable<boolean> {
    // Check dynamic settings first, fall back to environment
    console.log('check firststart');
    if (!this.featureSettingsService.isFeatureEnabled('feature_firststart')) {
      console.log('no firststart');
      return of(true);
    }

    // Check if the user is logged in
    let firststart_done = false;
    if (this.authRepository.getUser() == null) {
      return of(false);
    }
    firststart_done = this.authRepository.getUser().firststart_done == 1;
    console.log('fiststart done: ' + firststart_done);

    return of(firststart_done);
  }

  /*
  xcheck() {
    if (this.authRepository.isLoggedIn()) {
      var wasHere = this.cookieService.get('logged-on-today');
      if (wasHere === '1') {
        // User has already visited today
        console.log('was-here-today OK');
      } else {
        this.cookieService.set('logged-on-today', '1', { expires: 60 * 60 * 12 });
        console.log('save login mit remember me');
        this.journalService.log('Anmeldung mit Remember Me Funktion    --------------------------------------');
      }

      // Uncomment below to enable additional feature checks or configurations
      /*
          // Now check if the first start configuration wizard has to be activated
          console.log("value: " + this.authQuery.getValue().appdata);
          return this.authQuery.selectFirst().pipe(
            map((data) => {
              let appData = <IAppDataModel>data;
              if (String(appData.activation.firststart_wizard).toLowerCase() === "true") {
                // Setup wizard is done
                console.log("it is done (true): " + appData.activation.modul1_paid);
                return true;
              } else {
                // Setup wizard is not done
                console.log("it is not done (false): " + appData.activation.modul1_paid);
                router.navigate(['/firststart'], { queryParams: { redirect: state.url }, replaceUrl: true });
                return false;
              }
            })
          );
          */

  // Check for other configurations or sync needs
  /*
          this.authQuery.selectEntity(this.authQuery.appid).subscribe((state) => {
            console.log("state received:", state);
            var data = this.authService.getAppDataVersion(state).subscribe((data) => {
              console.log('current version:', this.authQuery.version, '/', data.version);
              if (this.authQuery.version < data.version) {
                console.log('new version available');
              }
            });
          });
          *

      let firststart = true; // Simplified example, replace with actual check
      if (firststart) {
        return true;
      } else {
        router.navigate(['/firststart'], {
          queryParams: { redirect: state.url },
          replaceUrl: true,
        });
        return false;
      }
    } else {
      log.debug('Not authenticated, redirecting and adding redirect url...');
      router.navigate(['/login'], {
        queryParams: { redirect: state.url },
        replaceUrl: true,
      });
      return false;
    }
  }
  */

  /**
   * Setter & getter for access token
   */
  set accessToken(token: string) {
    localStorage.setItem('accessToken', token);
  }

  get accessToken(): string {
    return localStorage.getItem('accessToken') ?? '';
  }

  /**
   * Sign in using the access token
   */
  signInUsingToken(): Observable<any> {
    console.log('sign in using token');

    return of(true);

    // Sign in using the token
    return this.http
      .post('api/auth/sign-in-with-token', {
        accessToken: this.accessToken,
      })
      .pipe(
        catchError(() =>
          // Return false
          of(false)
        ),
        switchMap((response: any) => {
          // Replace the access token with the new one if it's available on
          // the response object.
          //
          // This is an added optional step for better security. Once you sign
          // in using the token, you should generate a new one on the server
          // side and attach it to the response object. Then the following
          // piece of code can replace the token with the refreshed one.
          if (response.accessToken) {
            this.accessToken = response.accessToken;
          }

          // Set the authenticated flag to true
          //this._authenticated = true;

          // Store the user on the user service
          //this._userService.user = response.user;

          // Return true
          return of(true);
        })
      );
  }

  /**
   * Unlock session
   *
   * @param credentials
   */
  unlockSession(credentials: { email: string; password: string }): Observable<any> {
    return this.http.post('api/auth/unlock-session', credentials);
  }

  private handleError(error: HttpErrorResponse) {
    this.logout();
    if (error.status === 0) {
      return throwError(() => new Error('An error occurred:', error.error));
    } else if (error.status === 400) {
      return throwError(() => new Error(`Incorrect input provided`));
    } else if (error.status === 401) {
      return throwError(() => new Error(`Incorrect email/password provided`));
    } else {
      return throwError(() => new Error(`Backend returned code ${error.status}`));
    }
  }
}
